import React, { useEffect, useRef, useState } from 'react'
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import { Button, Alert } from 'components/ui'

// How often to re-check while the QR is on screen, and for how long. A QR PH
// payment normally clears within seconds of the payer confirming in their app;
// after the window the Check button and the backend's own 20-minute auto-check
// take over, so an idle tab doesn't poll the gateway forever.
const POLL_MS = 5000
const POLL_FOR_MS = 15 * 60 * 1000

// Statuses that end the wait. Anything else (PENDING, PROCESSING, UNKNOWN)
// keeps polling.
const DONE = ['SUCCESS', 'FAILED', 'CANCELED', 'EXPIRED']

/**
 * A QR PH code to pay by scanning with any bank or e-wallet app, plus the
 * status check that notices when it has been paid.
 *
 * `onCheck` re-checks the payment and resolves to our normalised status
 * string; `onDone(status)` fires once when it reaches a final one. The panel
 * never decides a payment succeeded by itself -- the backend does, from the
 * gateway's own status endpoint.
 *
 * Save QR exists for members on a phone: they can't scan their own screen,
 * but every PH bank/e-wallet app can scan a QR from the photo gallery.
 */
const QrPayPanel = ({ qrString, amountLabel, onCheck, onDone }) => {
    const [status, setStatus] = useState('PENDING')
    const [checking, setChecking] = useState(false)
    const [timedOut, setTimedOut] = useState(false)
    const canvasWrap = useRef(null)
    const doneRef = useRef(false)
    const startedAt = useRef(Date.now())

    const check = async () => {
        if (doneRef.current) return
        setChecking(true)
        try {
            const next = await onCheck()
            if (next) setStatus(next)
            if (DONE.includes(next) && !doneRef.current) {
                doneRef.current = true
                onDone?.(next)
            }
        } catch (e) {
            // A failed check is not a failed payment; keep waiting.
        }
        setChecking(false)
    }

    useEffect(() => {
        const timer = setInterval(() => {
            if (doneRef.current) {
                clearInterval(timer)
                return
            }
            if (Date.now() - startedAt.current > POLL_FOR_MS) {
                setTimedOut(true)
                clearInterval(timer)
                return
            }
            check()
        }, POLL_MS)
        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSave = () => {
        const canvas = canvasWrap.current?.querySelector('canvas')
        if (!canvas) return
        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/png')
        link.download = 'payment-qr.png'
        link.click()
    }

    const done = DONE.includes(status)

    return (
        <div className="flex flex-col items-center gap-3 text-center">
            {amountLabel ? <div className="text-2xl font-semibold">{amountLabel}</div> : null}

            <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={qrString} size={220} includeMargin={false} />
            </div>
            {/* Higher-resolution copy for Save QR; never shown. */}
            <div ref={canvasWrap} className="hidden">
                <QRCodeCanvas value={qrString} size={600} includeMargin />
            </div>

            <p className="text-sm text-gray-500 max-w-xs">
                Scan with GCash, Maya or any bank app that supports QR Ph. On your phone?
                Save the QR, then choose "upload from gallery" in your app.
            </p>

            {status === 'SUCCESS' && (
                <Alert type="success" showIcon>Payment received.</Alert>
            )}
            {done && status !== 'SUCCESS' && (
                <Alert type="danger" showIcon>
                    This payment did not go through ({status.toLowerCase()}). You have not been charged
                    unless your app says otherwise.
                </Alert>
            )}
            {!done && (
                <span className="text-xs text-gray-400">
                    {timedOut
                        ? 'Stopped checking automatically. Press Check payment once you have paid.'
                        : 'Waiting for payment… this updates by itself.'}
                </span>
            )}

            <div className="flex gap-2">
                <Button size="sm" onClick={onSave}>Save QR</Button>
                {!done && (
                    <Button size="sm" variant="solid" loading={checking} onClick={check}>
                        Check payment
                    </Button>
                )}
            </div>
        </div>
    )
}

export default QrPayPanel
